"use client";

import { useRef, useState } from "react";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { useDebounce } from "@/hooks/use-debounce";
import { useToolComputed } from "@/hooks/use-tool-computed";
import { trackToolUsed } from "@/lib/analytics/tool-events";
import {
  checkablePostalCode,
  marketBand,
  type CheckSide,
  type MarketBand,
} from "./price-check-model";

const INPUT_SETTLE_MS = 300;

export type PriceCheckInputs = {
  productTypeSlug: string;
  side: CheckSide;
  postalCode: string;
  condition: string;
  sizeKey: string;
  priceCents: number | null;
};

export type PriceCheckDefaults = Partial<Omit<PriceCheckInputs, "productTypeSlug">> & {
  productTypeSlug: string;
  stateSlug?: string;
};

export type PriceCheckState = {
  inputs: PriceCheckInputs;
  band: MarketBand | null;
  isLoading: boolean;
  place: { code: string; name: string; slug: string } | null;
  update: (change: Partial<PriceCheckInputs>) => void;
};

function useMarketBand(inputs: PriceCheckInputs, stateSlug: string | undefined) {
  const settledInputs = useDebounce(inputs, INPUT_SETTLE_MS);
  const postalCode = checkablePostalCode(settledInputs.postalCode);
  const result = useQuery(api.priceIndex.checkPrice.checkPrice, {
    productTypeSlug: settledInputs.productTypeSlug,
    condition: settledInputs.condition || undefined,
    sizeKey: settledInputs.sizeKey || undefined,
    postalCode,
    stateSlug: postalCode ? undefined : stateSlug,
  });
  const [lastResult, setLastResult] = useState(result);
  if (result !== undefined && result !== lastResult) setLastResult(result);
  return { result: result ?? lastResult, isLoading: result === undefined };
}

function computationKey(inputs: PriceCheckInputs, band: MarketBand | null): string | null {
  if (!band || inputs.priceCents === null) return null;
  return [
    inputs.productTypeSlug,
    inputs.side,
    band.placeCode,
    inputs.condition,
    inputs.sizeKey,
    inputs.priceCents,
  ].join("|");
}

export function usePriceCheck(defaults: PriceCheckDefaults): PriceCheckState {
  const [inputs, setInputs] = useState<PriceCheckInputs>({
    side: "buy",
    postalCode: "",
    condition: "",
    sizeKey: "",
    priceCents: null,
    ...defaults,
  });
  const hasStarted = useRef(false);
  const { result, isLoading } = useMarketBand(inputs, defaults.stateSlug);
  const band = marketBand(result);
  useToolComputed("price-check", computationKey(inputs, band), inputs.productTypeSlug);
  const update = (change: Partial<PriceCheckInputs>) => {
    if (!hasStarted.current) {
      hasStarted.current = true;
      trackToolUsed({ tool: "price-check", tool_step: "started", productTypeSlug: inputs.productTypeSlug });
    }
    setInputs((current) => ({ ...current, ...change }));
  };
  return { inputs, band, isLoading, place: result?.place ?? null, update };
}
